// Font parity management and verification

export interface FontDescriptor {
  family: string;
  weight: number;
  style: string;
  features: string[]; // OpenType features like ['kern', 'liga']
  source: 'backend' | 'system' | 'webfont';
  hash?: string; // Font file hash for verification
}

export interface FontParityResult {
  isMatch: boolean;
  frontend: FontDescriptor;
  backend: FontDescriptor;
  differences: string[];
  recommendation: 'use_backend' | 'use_webfont' | 'fallback_local';
}

export class FontParityManager {
  private static loadedWebFonts = new Map<string, FontDescriptor>();
  private static fontFaceCache = new Map<string, FontFace>();

  /**
   * Verify font parity between frontend and backend
   */
  static async verifyFontParity(
    backendFont: FontDescriptor,
    requestedFont: { family: string; size: number }
  ): Promise<FontParityResult> {
    const differences: string[] = [];

    // Get frontend font capabilities
    const frontendFont = await this.analyzeFrontendFont(requestedFont.family);

    // Compare font families
    if (frontendFont.family !== backendFont.family) {
      differences.push(`Font family mismatch: frontend="${frontendFont.family}", backend="${backendFont.family}"`);
    }

    // Compare OpenType features
    const frontendFeatures = new Set(frontendFont.features);
    const backendFeatures = new Set(backendFont.features);

    for (const feature of backendFeatures) {
      if (!frontendFeatures.has(feature)) {
        differences.push(`Missing frontend OpenType feature: ${feature}`);
      }
    }

    // Determine recommendation
    let recommendation: FontParityResult['recommendation'] = 'use_backend';
    
    if (differences.length > 0) {
      if (backendFont.source === 'webfont' && backendFont.hash) {
        recommendation = 'use_webfont';
      } else {
        recommendation = 'fallback_local';
      }
    }

    return {
      isMatch: differences.length === 0,
      frontend: frontendFont,
      backend: backendFont,
      differences,
      recommendation
    };
  }

  /**
   * Load exact font from backend via @font-face
   */
  static async loadBackendFont(
    fontDescriptor: FontDescriptor,
    fontUrl: string
  ): Promise<boolean> {
    try {
      // Create unique family name to avoid conflicts
      const uniqueFamilyName = `${fontDescriptor.family}_HB_${fontDescriptor.hash?.slice(0, 8)}`;

      // Check if already loaded
      if (this.loadedWebFonts.has(uniqueFamilyName)) {
        return true;
      }

      // Create @font-face declaration
      const fontFace = new FontFace(uniqueFamilyName, `url(${fontUrl})`, {
        weight: fontDescriptor.weight.toString(),
        style: fontDescriptor.style,
        display: 'block' // Ensure font loads before rendering
      });

      // Load the font
      await fontFace.load();
      
      // Add to document fonts
      document.fonts.add(fontFace);

      // Cache the loaded font
      this.loadedWebFonts.set(uniqueFamilyName, {
        ...fontDescriptor,
        family: uniqueFamilyName,
        source: 'webfont'
      });

      this.fontFaceCache.set(uniqueFamilyName, fontFace);

      console.log(`✅ [FontParityManager] Loaded backend font: ${uniqueFamilyName}`);
      return true;

    } catch (error) {
      console.error(`❌ [FontParityManager] Failed to load backend font:`, error);
      return false;
    }
  }

  /**
   * Get the exact font family name to use for rendering
   */
  static getFontFamilyForRendering(
    backendFont: FontDescriptor,
    parityResult: FontParityResult
  ): string {
    switch (parityResult.recommendation) {
      case 'use_webfont':
        // Use the unique webfont family name
        const uniqueName = `${backendFont.family}_HB_${backendFont.hash?.slice(0, 8)}`;
        return this.loadedWebFonts.has(uniqueName) ? uniqueName : backendFont.family;
      
      case 'use_backend':
        return backendFont.family;
      
      case 'fallback_local':
      default:
        return backendFont.family;
    }
  }

  /**
   * Configure canvas context with exact font properties
   */
  static configureCanvasFont(
    ctx: CanvasRenderingContext2D,
    fontDescriptor: FontDescriptor,
    fontSize: number
  ): void {
    const fontFamily = this.getFontFamilyForRendering(fontDescriptor, {
      isMatch: true,
      frontend: fontDescriptor,
      backend: fontDescriptor,
      differences: [],
      recommendation: 'use_backend'
    });

    // Build font string with exact properties
    const fontString = `${fontDescriptor.style} ${fontDescriptor.weight} ${fontSize}px "${fontFamily}"`;
    ctx.font = fontString;

    // Enable font features if supported
    if ('fontVariantLigatures' in ctx) {
      (ctx as any).fontVariantLigatures = fontDescriptor.features.includes('liga') ? 'normal' : 'none';
    }

    if ('fontKerning' in ctx) {
      (ctx as any).fontKerning = fontDescriptor.features.includes('kern') ? 'normal' : 'none';
    }

    console.log(`🎯 [FontParityManager] Configured canvas font: ${fontString}`);
  }

  /**
   * Analyze frontend font capabilities
   */
  private static async analyzeFrontendFont(fontFamily: string): Promise<FontDescriptor> {
    // Create test canvas to analyze font
    const testCanvas = document.createElement('canvas');
    const testCtx = testCanvas.getContext('2d')!;
    
    testCtx.font = `12px "${fontFamily}"`;
    
    // Test for OpenType feature support
    const features: string[] = [];
    
    // Test kerning support
    const withKerning = testCtx.measureText('AV').width;
    testCtx.font = `12px "${fontFamily}"`;
    if ('fontKerning' in testCtx) {
      (testCtx as any).fontKerning = 'none';
    }
    const withoutKerning = testCtx.measureText('AV').width;
    
    if (withKerning !== withoutKerning) {
      features.push('kern');
    }

    // Test ligature support (simplified)
    testCtx.font = `12px "${fontFamily}"`;
    const ligatureTest = testCtx.measureText('fi').width;
    const separateTest = testCtx.measureText('f').width + testCtx.measureText('i').width;
    
    if (Math.abs(ligatureTest - separateTest) > 0.1) {
      features.push('liga');
    }

    return {
      family: fontFamily,
      weight: 400, // Default weight
      style: 'normal',
      features,
      source: 'system'
    };
  }

  /**
   * Validate font rendering consistency
   */
  static validateRenderingConsistency(
    text: string,
    fontDescriptor: FontDescriptor,
    fontSize: number
  ): {
    isConsistent: boolean;
    frontendWidth: number;
    expectedWidth?: number;
    difference?: number;
  } {
    const testCanvas = document.createElement('canvas');
    const testCtx = testCanvas.getContext('2d')!;

    this.configureCanvasFont(testCtx, fontDescriptor, fontSize);
    const frontendWidth = testCtx.measureText(text).width;

    return {
      isConsistent: true, // Would need backend comparison
      frontendWidth
    };
  }
}
